/**
 * Field overlays painted on top of `DocumentViewer` pages.
 *
 * The overlay layer is rendered via the viewer's `renderOverlay` slot,
 * which gives us one mount per visible page plus the page's scale,
 * rotation, and rendered size. Field anchors are stored in document space
 * (intrinsic CSS px) so we transform them through `documentRectToView`
 * — that path is unit-tested and benchmarked, so we get correct
 * positioning across scroll, zoom, and rotation for free.
 *
 * Click → opens a small inline editor anchored to the field box. The
 * editor commits via the `usePatchSingleField` mutation; transient
 * state (saving / saved / error) is surfaced as a corner badge on the
 * overlay so the user always sees where the change is in its lifecycle.
 *
 * Performance: when the viewer signals `scrolling=true` we skip the
 * editor portal (it doesn't matter if the popup briefly disappears
 * during a fast scroll) and render the overlay as cheap divs only.
 */
import {
  memo,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
  type KeyboardEvent,
  type ReactNode,
} from 'react'
import { Check, Loader2, X } from 'lucide-react'

import type { Field, PatchSingleFieldBody } from '@/api/docfiller-types'
import {
  fieldDisplayName,
  isCheckboxChecked,
} from '@/api/docfiller-types'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { cn } from '@/lib/utils'

import { anchorRectToDocumentRect, type FieldAnchor } from './fieldAnchor'
import type { FieldEditingApi } from './useFieldEditing'
import { documentRectToView } from './viewer/coordinates'
import type { PageModel, Rotation } from './viewer/types'

export interface FieldOverlayPerPageProps {
  /** The page whose overlay layer is being rendered. */
  page: PageModel
  /** Current zoom factor passed by the viewer shell. */
  scale: number
  /** Current rotation passed by the viewer shell. */
  rotation: Rotation
  /** Rendered page size in CSS pixels after zoom and rotation. */
  rendered: { width: number; height: number }
  /**
   * Active-scroll signal. When true we render overlays as pointer-events-none
   * so a fast scroll never gets eaten by a hover ring; the editor popup
   * also collapses while scrolling and re-opens once it settles.
   */
  scrolling: boolean
  /** All fields anchored to this page. Pre-filtered by the consumer. */
  fields: Field[]
  /** Shared editing state lifted from the route. */
  editing: FieldEditingApi
  /** Per-field PATCH mutation supplied by the parent (already bound to docId). */
  savePatch: (
    fieldId: string,
    body: PatchSingleFieldBody,
  ) => Promise<Field>
}

/**
 * The `renderOverlay` slot on `DocumentViewer` is called per visible page,
 * so this is the per-page entry. Keep it cheap: it iterates only the
 * fields that already belong to this page.
 */
function FieldOverlayPerPageInner({
  page,
  scale,
  rotation,
  rendered,
  scrolling,
  fields,
  editing,
  savePatch,
}: FieldOverlayPerPageProps) {
  // The viewer's overlay slot sets `pointer-events-none` on its wrapper,
  // so we re-enable pointer events on each overlay box explicitly. The
  // wrapper-disabled pattern means an empty page never blocks scroll
  // gestures, and our boxes opt back in.
  return (
    <>
      {fields.map((field) => {
        const docRect = anchorRectToDocumentRect(
          field.anchor as FieldAnchor | null,
          page.intrinsic,
        )
        if (!docRect) return null
        return (
          <FieldBox
            key={field.uuid}
            field={field}
            docRect={docRect}
            page={page}
            scale={scale}
            rotation={rotation}
            rendered={rendered}
            scrolling={scrolling}
            editing={editing}
            savePatch={savePatch}
          />
        )
      })}
    </>
  )
}

export const FieldOverlayPerPage = memo(FieldOverlayPerPageInner)

interface FieldBoxProps {
  field: Field
  docRect: { x: number; y: number; width: number; height: number }
  page: PageModel
  scale: number
  rotation: Rotation
  rendered: { width: number; height: number }
  scrolling: boolean
  editing: FieldEditingApi
  savePatch: (
    fieldId: string,
    body: PatchSingleFieldBody,
  ) => Promise<Field>
}

function FieldBox({
  field,
  docRect,
  page,
  scale,
  rotation,
  rendered,
  scrolling,
  editing,
  savePatch,
}: FieldBoxProps) {
  const isSelected = editing.selectedFieldId === field.uuid
  const isDirty = editing.dirty.has(field.uuid)
  const status = editing.statusFor(field.uuid)
  const value = editing.valueFor(field)

  // Transform once per render — cheap, the helper is benched.
  const viewRect = documentRectToView(docRect, page.intrinsic, scale, rotation)

  const hasPendingSuggestion =
    field.suggested_value != null &&
    field.suggested_value !== '' &&
    field.suggested_value !== field.current_value
  const isAiFilled = field.accepted_by === 'agent' && !isDirty
  const hasError = status.kind === 'error'
  const ariaLabel = `Edit field: ${fieldDisplayName(field)}`

  const openEditor = useCallback(() => {
    editing.selectField(field.uuid)
  }, [editing, field.uuid])

  const onKey = useCallback(
    (e: KeyboardEvent<HTMLButtonElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault()
        openEditor()
      }
    },
    [openEditor],
  )

  return (
    <>
      <button
        type="button"
        data-testid={`field-overlay-${field.uuid}`}
        data-field-uuid={field.uuid}
        data-field-state={
          hasError
            ? 'error'
            : status.kind === 'saving'
              ? 'saving'
              : status.kind === 'saved'
                ? 'saved'
                : isDirty
                  ? 'dirty'
                  : isSelected
                    ? 'selected'
                    : 'idle'
        }
        aria-label={ariaLabel}
        aria-pressed={isSelected}
        onClick={openEditor}
        onKeyDown={onKey}
        // The viewer's overlay wrapper is pointer-events-none so it never
        // eats scroll. Re-enable pointer events on the actual hit target.
        className={cn(
          'pointer-events-auto absolute box-border cursor-pointer touch-manipulation rounded-[3px] border outline-none transition-[border-color,box-shadow,background-color]',
          'focus-visible:ring-2 focus-visible:ring-ring/60 focus-visible:ring-offset-1',
          // Tone by lifecycle, in priority order
          hasError && 'border-destructive bg-destructive/10 shadow-[0_0_0_1px_var(--color-destructive)]',
          !hasError && status.kind === 'saving' && 'border-info bg-info/10',
          !hasError && status.kind === 'saved' && 'border-success bg-success/10',
          !hasError &&
            status.kind === 'idle' &&
            isDirty &&
            'border-warning bg-warning/10 shadow-[0_0_0_1px_var(--color-warning)]',
          !hasError &&
            status.kind === 'idle' &&
            !isDirty &&
            hasPendingSuggestion &&
            'border-warning/60 bg-transparent hover:bg-warning/10',
          !hasError &&
            status.kind === 'idle' &&
            !isDirty &&
            !hasPendingSuggestion &&
            isAiFilled &&
            'border-success/50 bg-transparent hover:bg-success/10',
          !hasError &&
            status.kind === 'idle' &&
            !isDirty &&
            !hasPendingSuggestion &&
            !isAiFilled &&
            'border-primary/35 bg-transparent hover:border-primary/70 hover:bg-primary/10',
          isSelected && 'border-primary bg-primary/10 shadow-[0_0_0_2px_var(--color-ring)]',
          // While scrolling, drop hover affordances entirely to keep
          // overlays out of the user's way during fast scroll bursts.
          scrolling && 'pointer-events-none transition-none',
        )}
        style={{
          left: `${viewRect.x}px`,
          top: `${viewRect.y}px`,
          width: `${Math.max(8, viewRect.width)}px`,
          height: `${Math.max(8, viewRect.height)}px`,
        }}
      >
        {(field.kind === 'signature' || field.kind === 'date') && value && (
          <span
            aria-hidden
            data-testid={`field-overlay-value-${field.uuid}`}
            title={value}
            className={cn(
              'pointer-events-none absolute inset-0 flex min-w-0 items-center overflow-hidden px-1 text-left text-[11px] font-medium leading-none text-foreground',
              field.kind === 'signature' && 'font-normal italic',
            )}
          >
            <span className="block w-full truncate">{value}</span>
          </span>
        )}
        <StatusBadge status={status} dirty={isDirty} hasSuggestion={hasPendingSuggestion} />
        {/* Screen-reader-only summary so assistive tech reads more than
            the bare "Edit field: …" label. */}
        <span className="sr-only">
          {isDirty
            ? `Unsaved value: ${value ?? '(empty)'}`
            : value
              ? `Current value: ${value}`
              : 'No value yet.'}
        </span>
      </button>

      {/* Inline editor popover — rendered inside the same page slot so it
          inherits the same scroll context and zoom transforms. While the
          viewer is actively scrolling we hide the popup; the next
          settled render restores it. */}
      {isSelected && !scrolling && (
        <FieldInlineEditor
          field={field}
          value={value}
          viewRect={viewRect}
          rendered={rendered}
          editing={editing}
          savePatch={savePatch}
        />
      )}
    </>
  )
}

interface StatusBadgeProps {
  status: ReturnType<FieldEditingApi['statusFor']>
  dirty: boolean
  hasSuggestion: boolean
}

function StatusBadge({ status, dirty, hasSuggestion }: StatusBadgeProps) {
  let label: string | null = null
  let icon: ReactNode = null
  let tone = 'bg-muted text-muted-foreground'
  if (status.kind === 'saving') {
    label = 'Saving'
    icon = <Loader2 className="size-3 animate-spin" aria-hidden />
    tone = 'bg-info text-info-foreground'
  } else if (status.kind === 'saved') {
    label = 'Saved'
    icon = <Check className="size-3" aria-hidden />
    tone = 'bg-success text-success-foreground'
  } else if (status.kind === 'error') {
    label = 'Error'
    icon = <X className="size-3" aria-hidden />
    tone = 'bg-destructive text-destructive-foreground'
  } else if (dirty) {
    label = 'Unsaved'
    tone = 'bg-warning text-warning-foreground'
  } else if (hasSuggestion) {
    label = 'Suggestion'
    tone = 'bg-warning-soft text-warning-soft-foreground'
  }
  if (!label) return null
  return (
    <span
      data-testid="field-overlay-status"
      data-field-status={status.kind === 'idle' ? (dirty ? 'dirty' : 'suggestion') : status.kind}
      className={cn(
        'pointer-events-none absolute -top-2 -right-1 z-10 flex items-center gap-1 rounded-sm px-1 py-px text-[10px] font-medium leading-none shadow-soft',
        tone,
      )}
    >
      {icon}
      {label}
    </span>
  )
}

interface FieldInlineEditorProps {
  field: Field
  value: string | null
  viewRect: { x: number; y: number; width: number; height: number }
  rendered: { width: number; height: number }
  editing: FieldEditingApi
  savePatch: (
    fieldId: string,
    body: PatchSingleFieldBody,
  ) => Promise<Field>
}

/**
 * Small popover anchored to the overlay box. Renders below the overlay if
 * there's room, otherwise above. Esc cancels, Enter / blur saves.
 *
 * For checkboxes we use the click on the overlay itself as the toggle
 * (no popup needed) — that branch returns null and the overlay's onClick
 * still flips the dirty buffer + commits. For everything else we render
 * a real input next to the box.
 */
function FieldInlineEditor({
  field,
  value,
  viewRect,
  rendered,
  editing,
  savePatch,
}: FieldInlineEditorProps) {
  const inputId = useId()
  const containerRef = useRef<HTMLDivElement | null>(null)
  const [draft, setDraft] = useState<string>(value ?? '')
  const [validation, setValidation] = useState(() => editing.validate(field, value))

  // Snapshot the shared dirty buffer at the moment this editor opens so
  // Cancel only discards the overlay session — not a pre-existing unsaved
  // value the user typed in the fields panel (or another overlay session).
  // The lazy initializer captures the state exactly once on mount.
  const [initialDirty] = useState<{ present: boolean; value: string | null }>(
    () => ({
      present: editing.dirty.has(field.uuid),
      value: editing.dirty.get(field.uuid) ?? null,
    }),
  )


  // Mirror the upstream value through the "previous-prop" pattern so the
  // editor re-syncs when the field gets re-fetched mid-edit.
  const [prevValue, setPrevValue] = useState(value)
  if (value !== prevValue) {
    setPrevValue(value)
    setDraft(value ?? '')
    setValidation(editing.validate(field, value))
  }

  // Focus the input as soon as the editor opens so the user can type
  // immediately. Esc / outside-click close via the document-level
  // listeners further down.
  useEffect(() => {
    const node = containerRef.current?.querySelector<HTMLInputElement | HTMLSelectElement>(
      'input,select',
    )
    node?.focus()
    if (node instanceof HTMLInputElement) {
      node.select()
    }
  }, [field.uuid])

  // Close on outside pointer-down so mouse and touch users get the same
  // behavior. The pointer event that landed outside still completes
  // naturally (e.g. clicking another overlay should select it).
  useEffect(() => {
    const onDocDown = (e: PointerEvent) => {
      const container = containerRef.current
      if (!container) return
      const target = e.target
      if (target instanceof Node && container.contains(target)) return
      // If the click landed on another field overlay, defer the selection
      // change so React's click handler on that overlay runs.
      editing.selectField(null)
    }
    document.addEventListener('pointerdown', onDocDown)
    return () => document.removeEventListener('pointerdown', onDocDown)
  }, [editing])

  const closeWithoutSaving = useCallback(() => {
    // Restore the pre-open dirty entry if there was one so cancelling the
    // overlay session never erases edits made via the fields panel.
    if (initialDirty.present) {
      editing.setDirtyValue(field.uuid, initialDirty.value)
    } else {
      editing.clearDirty(field.uuid)
    }
    editing.selectField(null)
  }, [editing, field.uuid, initialDirty])

  const commit = useCallback(
    async (raw: string | null) => {
      const next = raw === '' ? null : raw
      const result = editing.validate(field, next)
      setValidation(result)
      if (!result.ok) {
        editing.setStatus(field.uuid, {
          kind: 'error',
          message: result.message ?? 'Invalid value.',
        })
        return
      }
      // Mirror the value into the dirty buffer so the panel reflects the
      // pending edit while the round-trip is in flight.
      editing.setDirtyValue(field.uuid, next)
      await editing.commitField(field, next, (body) => savePatch(field.uuid, body))
      editing.selectField(null)
    },
    [editing, field, savePatch],
  )

  const onKeyDown = useCallback(
    (e: KeyboardEvent<HTMLDivElement | HTMLInputElement>) => {
      if (e.key === 'Escape') {
        e.preventDefault()
        e.stopPropagation()
        closeWithoutSaving()
        return
      }
      if (e.key === 'Enter' && field.kind !== 'signature') {
        e.preventDefault()
        e.stopPropagation()
        void commit(draft)
      }
    },
    [closeWithoutSaving, commit, draft, field.kind],
  )

  // Position the popup near the overlay while clamping it inside the page
  // slot. Mobile fit-to-width can leave fields close to the right edge.
  const popupWidth = Math.min(320, Math.max(220, viewRect.width))
  const popupLeft = Math.min(
    Math.max(4, viewRect.x),
    Math.max(4, rendered.width - popupWidth - 4),
  )
  const estimatedHeight = validation.ok ? 112 : 132
  const belowTop = viewRect.y + viewRect.height + 4
  const aboveTop = viewRect.y - estimatedHeight - 4
  const popupTop =
    belowTop + estimatedHeight <= rendered.height
      ? belowTop
      : Math.max(4, aboveTop)

  return (
    <div
      ref={containerRef}
      role="dialog"
      aria-label={`Edit ${fieldDisplayName(field)}`}
      data-testid={`field-overlay-editor-${field.uuid}`}
      // `pointer-events-auto` because the parent overlay wrapper sets
      // pointer-events-none for the slot.
      className="pointer-events-auto absolute z-50 rounded-md border border-border bg-popover p-2 text-popover-foreground shadow-floating"
      style={{
        top: `${popupTop}px`,
        left: `${popupLeft}px`,
        width: `${popupWidth}px`,
        maxWidth: `calc(100% - ${popupLeft + 4}px)`,
      }}
      onKeyDown={onKeyDown}
      // Prevent the document pointer-down listener above from closing this
      // popup when the user clicks inside it.
      onPointerDown={(e) => e.stopPropagation()}
    >
      <label
        htmlFor={inputId}
        className="mb-1 block text-[11px] font-medium tracking-tight text-muted-foreground"
      >
        {fieldDisplayName(field)}
      </label>
      {renderEditorControl(field, inputId, draft, setDraft)}
      {!validation.ok && validation.message && (
        <p
          role="alert"
          data-testid={`field-overlay-editor-error-${field.uuid}`}
          className="mt-1 text-[11px] text-destructive"
        >
          {validation.message}
        </p>
      )}
      {field.suggested_value != null &&
        field.suggested_value !== '' &&
        field.suggested_value !== field.current_value && (
          <p className="mt-1 text-[11px] text-muted-foreground">
            Suggestion:{' '}
            <button
              type="button"
              data-testid={`field-overlay-apply-suggestion-${field.uuid}`}
              className="font-mono font-medium text-foreground underline-offset-2 hover:underline"
              onClick={() => {
                setDraft(field.suggested_value ?? '')
              }}
            >
              {field.suggested_value}
            </button>
          </p>
        )}
      <div className="mt-2 flex justify-end gap-1">
        <Button
          type="button"
          size="sm"
          variant="ghost"
          className="h-7 px-2 text-xs"
          onClick={closeWithoutSaving}
          data-testid={`field-overlay-cancel-${field.uuid}`}
        >
          Cancel
        </Button>
        <Button
          type="button"
          size="sm"
          className="h-7 px-2 text-xs"
          onClick={() => {
            void commit(draft)
          }}
          data-testid={`field-overlay-save-${field.uuid}`}
        >
          Save
        </Button>
      </div>
    </div>
  )
}

function renderEditorControl(
  field: Field,
  id: string,
  draft: string,
  setDraft: (v: string) => void,
) {
  switch (field.kind) {
    case 'checkbox': {
      const checked = isCheckboxChecked(draft)
      return (
        <label className="flex items-center gap-2 text-xs text-muted-foreground">
          <input
            id={id}
            type="checkbox"
            data-testid={`field-overlay-input-${field.uuid}`}
            checked={checked}
            onChange={(e) => setDraft(e.target.checked ? 'true' : 'false')}
            className="size-4 rounded border-input accent-primary"
          />
          <span>{checked ? 'Checked' : 'Unchecked'}</span>
        </label>
      )
    }
    case 'date':
      return (
        <Input
          id={id}
          type="date"
          data-testid={`field-overlay-input-${field.uuid}`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          className="h-8 text-xs"
        />
      )
    case 'signature':
      return (
        <Input
          id={id}
          type="text"
          data-testid={`field-overlay-input-${field.uuid}`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder="Type a typed signature"
          className="h-8 italic text-xs"
        />
      )
    case 'text':
    default:
      return (
        <Input
          id={id}
          type="text"
          data-testid={`field-overlay-input-${field.uuid}`}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          placeholder={field.suggested_value ?? ''}
          className="h-8 text-xs"
        />
      )
  }
}
